import React from 'react';
import { Avatar, Box, Typography, useTheme } from '@ublend-npm/aulaui-core';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { HOME_WIDGET_FONT_FAMILY } from '../../homeWidgetTypography';
import { OutlookMessage, formatDisplayDate } from '../types';

type OutlookEmailRowProps = {
  message: OutlookMessage;
  isSelected?: boolean;
  /** Compact mode trims row height and hides some padding (used inside dialogs). */
  compact?: boolean;
  onClick: (message: OutlookMessage) => void;
};

/**
 * A single email list row — shared between the inbox preview and the
 * "all emails" dialog.  Renders sender avatar, subject, preview, date
 * and optional importance/attachment icons.
 */
const OutlookEmailRow = ({
  message,
  isSelected = false,
  compact = false,
  onClick,
}: OutlookEmailRowProps) => {
  const { palette } = useTheme();
  const isDarkMode = palette.mode === 'dark';

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick(message);
    }
  };

  return (
    <Box
      display="flex"
      gap={compact ? 1.5 : 1}
      alignItems="flex-start"
      width="100%"
      height={compact ? undefined : '89px'}
      py={compact ? 1.75 : 1}
      px={compact ? 1.5 : { xs: 1, sm: 2 }}
      mb={!compact && message.isUnread ? 1 : 0}
      sx={{
        cursor: 'pointer',
        boxSizing: 'border-box',
        borderRadius: '8px',
        bgcolor: isSelected
          ? isDarkMode
            ? '#000000'
            : 'rgba(69, 33, 136, 0.08)'
          : 'transparent',
        '&:hover': {
          bgcolor: isSelected
            ? isDarkMode
              ? '#000000'
              : 'rgba(69, 33, 136, 0.08)'
            : isDarkMode
            ? '#000000'
            : 'rgba(69, 33, 136, 0.04)',
        },
      }}
      role="button"
      tabIndex={0}
      onClick={() => onClick(message)}
      onKeyDown={handleKeyDown}
    >
      {/* Unread dot + avatar */}
      <Box
        display="flex"
        alignItems="center"
        gap={{ xs: 0.5, sm: 0.75 }}
        flexShrink={0}
      >
        <Box
          width={8}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {message.isUnread ? (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: '#0277BD',
              }}
            />
          ) : null}
        </Box>

        <Avatar
          fallbackText={message.senderInitials}
          variant="circular"
          sx={{
            bgcolor: isDarkMode ? '#D7D2DF' : message.senderColor,
            width: { xs: compact ? 40 : '36px', sm: '40px' },
            height: { xs: compact ? 40 : '36px', sm: '40px' },
            minWidth: { xs: compact ? 40 : '36px', sm: '40px' },
            minHeight: { xs: compact ? 40 : '36px', sm: '40px' },
            fontSize: '16px',
            flexShrink: 0,
          }}
        />
      </Box>

      {/* Text content */}
      <Box display="flex" flexDirection="column" minWidth={0} flex={1}>
        {/* Row 1: sender name + icons */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-start"
            gap={0.5}
          >
            <Typography
              variant="subtitle1"
              noWrap
              sx={{
                minWidth: 0,
                flex: 1,
                fontFamily: HOME_WIDGET_FONT_FAMILY,
                fontSize: '16px',
                lineHeight: '20px',
                letterSpacing: '0.15px',
                fontWeight: message.isUnread ? 600 : 400,
                color: isDarkMode ? '#FFFFFF' : 'rgba(0, 0, 0, 0.87)',
              }}
            >
              {message.senderName}
            </Typography>
            {/* Thread count chip — only shown when there are replies */}
            {message.replyCount != null && message.replyCount > 0 ? (
              <Typography
                sx={{
                  borderRadius: 1,
                  fontSize: '12px',
                  paddingInline: '6px',
                  bgcolor: palette.grey[500],
                  color: '#FFFFFF',
                }}
                variant="body2"
              >
                {message.replyCount + 1}
              </Typography>
            ) : null}
          </Box>

          <Box display="flex" alignItems="center" gap={0.75} flexShrink={0}>
            {message.isImportant ? (
              <PriorityHighIcon sx={{ color: '#D32F2F', fontSize: 20 }} />
            ) : null}
            {message.hasAttachment ? (
              <AttachFileIcon
                sx={{
                  color: isDarkMode ? 'rgb(208, 188, 250)' : '#6B6B6B',
                  fontSize: 22,
                }}
              />
            ) : null}
          </Box>
        </Box>

        {/* Row 2: subject+ date */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          mt={compact ? 0.25 : 0.4}
        >
          <Box
            display="flex"
            alignItems="center"
            gap={0.75}
            minWidth={0}
            flex={1}
          >
            <Typography
              variant="body1"
              noWrap
              sx={{
                minWidth: 0,
                flex: 1,
                fontFamily: HOME_WIDGET_FONT_FAMILY,
                fontSize: '14px',
                lineHeight: '20px',
                letterSpacing: '0.14px',
                fontWeight: message.isUnread ? 600 : 400,
                color: message.isUnread
                  ? isDarkMode
                    ? '#03A9F4'
                    : '#01579B'
                  : isDarkMode
                  ? '#FFFFFF'
                  : 'rgba(0, 0, 0, 0.87)',
              }}
            >
              {message.subject}
            </Typography>
          </Box>

          <Typography
            variant="body2"
            color="text.secondary"
            noWrap
            sx={{
              fontFamily: HOME_WIDGET_FONT_FAMILY,
              fontSize: '12px',
              lineHeight: '16px',
              flexShrink: 0,
            }}
          >
            {formatDisplayDate(message)}
          </Typography>
        </Box>

        {/* Row 3: preview */}
        <Typography
          variant="body1"
          noWrap
          mt={compact ? 0.25 : 0.5}
          sx={{
            width: '100%',
            fontFamily: HOME_WIDGET_FONT_FAMILY,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0px',
            fontWeight: 400,
            color: isDarkMode ? 'text.secondary' : 'rgba(0, 0, 0, 0.6)',
          }}
        >
          {message.preview}
        </Typography>
      </Box>
    </Box>
  );
};

export default OutlookEmailRow;
